/**
 * Extraction Grouping Service
 *
 * Functions for grouping documents in the Data Extraction tab,
 * mirroring the Encompass eFolder grouping system.
 */

import { DOCUMENT_TYPES } from '@/config/document-types';
import {
  getEncompassGroupForCategory,
  getGroupOrder,
  formatGroupDisplayName
} from '@/config/document-type-groups';
import {
  isProcessingStatus,
  isCompletedStatus,
  isFailedStatus,
  isUnprocessedStatus,
  type ExtractionDocumentGroup,
  type GroupStats
} from '@/types/extraction-grouping';
import type {
  DocumentWithExtraction,
  EncompassAttachmentWithExtraction,
  ExtractionStatus
} from '@/hooks/useExtractionData';

/**
 * Get the category for a document type key
 */
function getCategoryForDocumentType(typeKey: string | null | undefined): string | null {
  if (!typeKey) {
return null;
}
  const config = DOCUMENT_TYPES[typeKey as keyof typeof DOCUMENT_TYPES];
  return config?.category || null;
}

/**
 * Calculate group statistics from a list of items with status
 */
export function calculateGroupStats(
  items: Array<{ status: ExtractionStatus | string | null | undefined }>
): GroupStats {
  let unprocessedCount = 0;
  let processingCount = 0;
  let completedCount = 0;
  let failedCount = 0;

  for (const item of items) {
    const status = item.status;
    if (isUnprocessedStatus(status)) {
      unprocessedCount++;
    } else if (isProcessingStatus(status)) {
      processingCount++;
    } else if (isCompletedStatus(status)) {
      completedCount++;
    } else if (isFailedStatus(status)) {
      failedCount++;
    }
  }

  return {
    totalCount: items.length,
    unprocessedCount,
    processingCount,
    completedCount,
    failedCount
  };
}

/**
 * Group Lendwise documents by their classification result category
 * Documents without classification go to "Unclassified"
 */
export function groupLendwiseDocuments(
  documents: DocumentWithExtraction[]
): ExtractionDocumentGroup<DocumentWithExtraction>[] {
  // Group documents by their Encompass group
  const groupMap = new Map<string, DocumentWithExtraction[]>();

  for (const doc of documents) {
    // Get the document type from classification result
    const typeKey = doc.classification_result?.type;
    // Get the category for that type
    const category = getCategoryForDocumentType(typeKey);
    // Map category to Encompass group name
    const groupName = getEncompassGroupForCategory(category);

    if (!groupMap.has(groupName)) {
      groupMap.set(groupName, []);
    }
    groupMap.get(groupName)!.push(doc);
  }

  // Convert to array with stats
  const groups: ExtractionDocumentGroup<DocumentWithExtraction>[] = [];

  for (const [groupName, docs] of groupMap.entries()) {
    const stats = calculateGroupStats(docs);

    groups.push({
      groupName,
      displayName: formatGroupDisplayName(groupName),
      documents: docs,
      ...stats,
      order: getGroupOrder(groupName)
    });
  }

  // Sort alphabetically, with Unclassified/Other Documents last (matches eFolder)
  groups.sort((a, b) => {
    if (a.groupName === 'Unclassified' || a.groupName === 'Other Documents') {
return 1;
}
    if (b.groupName === 'Unclassified' || b.groupName === 'Other Documents') {
return -1;
}
    return a.displayName.localeCompare(b.displayName);
  });

  return groups;
}

/**
 * Group Encompass attachments by their parent document's document_groups
 * Requires a map from encompass_document_id to document_groups array
 */
export function groupEncompassAttachments(
  attachments: EncompassAttachmentWithExtraction[],
  documentGroupsMap: Map<string, string[]>
): ExtractionDocumentGroup<EncompassAttachmentWithExtraction>[] {
  // Group attachments by their parent document's primary group
  const groupMap = new Map<string, EncompassAttachmentWithExtraction[]>();

  for (const att of attachments) {
    // Get the document groups for the parent document
    const docGroups = documentGroupsMap.get(att.document_id);
    // Use the first group (primary), or "Unclassified" if none
    const groupName = (docGroups && docGroups.length > 0)
      ? docGroups[0]
      : 'Unclassified';

    if (!groupMap.has(groupName)) {
      groupMap.set(groupName, []);
    }
    groupMap.get(groupName)!.push(att);
  }

  // Convert to array with stats
  const groups: ExtractionDocumentGroup<EncompassAttachmentWithExtraction>[] = [];

  for (const [groupName, atts] of groupMap.entries()) {
    const stats = calculateGroupStats(atts);

    groups.push({
      groupName,
      displayName: formatGroupDisplayName(groupName),
      documents: atts,
      ...stats,
      order: getGroupOrder(groupName)
    });
  }

  // Sort alphabetically, with Unclassified/Other Documents last (matches eFolder)
  groups.sort((a, b) => {
    if (a.groupName === 'Unclassified' || a.groupName === 'Other Documents') {
return 1;
}
    if (b.groupName === 'Unclassified' || b.groupName === 'Other Documents') {
return -1;
}
    return a.displayName.localeCompare(b.displayName);
  });

  return groups;
}

/**
 * Filter groups to only include non-empty ones
 */
export function filterEmptyGroups<T>(
  groups: ExtractionDocumentGroup<T>[]
): ExtractionDocumentGroup<T>[] {
  return groups.filter(g => g.totalCount > 0);
}
